import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, MapPin, Scissors, MessageCircle, Navigation } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useInfo } from "@/context/InfoContext";
import { socialLinks } from "@/components/site/social";
import { ADDRESS, DAY_NAMES, MAPS_EMBED, MAPS_URL, euro, waLink } from "@/lib/api";

const Overline = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.3em] font-semibold text-zinc-500 mb-4">{children}</p>
);

const Hero = () => (
  <section className="relative" data-testid="hero-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-16 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
      <div className="lg:col-span-6 fade-up">
        <Overline>Donostia / San Sebastián</Overline>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#111111]" data-testid="hero-title">
          P7 BARBING<br /><span className="italic font-normal">Salon</span>
        </h1>
        <p className="mt-8 text-base sm:text-lg text-zinc-600 max-w-md leading-relaxed" data-testid="hero-slogan">
          «Estilo, precisión y elegancia en cada corte».
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/reservar" data-testid="hero-book-button" className="group h-14 px-8 inline-flex items-center gap-3 bg-[#111111] text-white text-xs tracking-[0.25em] uppercase hover:bg-zinc-800 transition-colors">
            Reservar cita <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <a href="#servicios" data-testid="hero-services-link" className="h-14 px-8 inline-flex items-center border border-zinc-300 text-xs tracking-[0.25em] uppercase hover:border-[#111111] transition-colors">
            Ver servicios
          </a>
        </div>
      </div>
      <div className="lg:col-span-6 relative fade-up" style={{ animationDelay: "120ms" }}>
        <div className="aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden bg-zinc-100">
          <img src="/gallery/hero.jpg" alt="Interior de barbería con sillones clásicos" className="w-full h-full object-cover grayscale-[30%] hover:grayscale-0 transition-[filter] duration-700" />
        </div>
        <div className="hidden sm:block absolute -bottom-6 -left-6 bg-white border border-zinc-200 px-6 py-5">
          <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Lunes a sábado</p>
          <p className="font-mono text-lg text-[#111111] mt-1">10:00 — 20:00</p>
        </div>
      </div>
    </div>
  </section>
);

const Services = ({ services }) => (
  <section id="servicios" className="py-20 sm:py-28 bg-[#F8F9FA] border-y border-zinc-100" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
        <div>
          <Overline>Servicios y precios</Overline>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">Nuestros servicios</h2>
        </div>
        <p className="text-sm text-zinc-500 max-w-xs">Elige tu servicio y reserva online tu cita.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {services.map((s, i) => (
          <div key={s.id} data-testid={`service-card-${i}`} className="group bg-white border border-zinc-200 p-8 sm:p-10 flex flex-col justify-between min-h-[260px] hover:border-[#111111] transition-colors duration-300">
            <div className="flex items-start justify-between gap-6">
              <div>
                <Scissors className="w-5 h-5 text-zinc-400 mb-6" />
                <h3 className="text-xl sm:text-2xl font-semibold tracking-tight" data-testid={`service-name-${i}`}>{s.name}</h3>
                <p className="mt-2 text-sm text-zinc-500 flex items-center gap-2"><Clock className="w-4 h-4" /> {s.duration} min</p>
              </div>
              <p className="font-mono text-3xl sm:text-4xl text-[#111111]" data-testid={`service-price-${i}`}>{euro(s.price)}</p>
            </div>
            <Link to={`/reservar?servicio=${s.id}`} data-testid={`service-book-${i}`} className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#111111] border-b border-[#111111] self-start pb-1 group-hover:gap-4 transition-[gap] duration-300">
              Reservar <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Gallery = ({ items }) => {
  const [active, setActive] = useState(null);
  if (!items.length) return null;
  return (
    <section id="galeria" className="py-20 sm:py-28" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Overline>Galería</Overline>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-12">Cortes y estilos</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {items.map((g, i) => (
            <button key={g.id} onClick={() => setActive(g)} data-testid={`gallery-item-${i}`} className={`overflow-hidden bg-zinc-100 group ${i % 5 === 0 ? "md:row-span-2 aspect-[3/4] md:aspect-auto" : "aspect-[3/4]"}`}>
              <img src={g.url} alt={g.alt || "Corte de pelo"} loading="lazy" className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.03] transition-[transform,filter] duration-700" />
            </button>
          ))}
        </div>
      </div>
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl p-0 border-0 bg-transparent shadow-none [&>button]:bg-white [&>button]:p-2 [&>button]:opacity-100 [&>button]:rounded-none" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">{active?.alt || "Imagen"}</DialogTitle>
          {active && <img src={active.url} alt={active.alt} className="w-full max-h-[85vh] object-contain bg-black" />}
          {active?.alt && <p className="text-white text-sm mt-2">{active.alt}</p>}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const HoursAndLocation = ({ settings }) => {
  const today = (new Date().getDay() + 6) % 7;
  const wa = waLink(settings.whatsapp);
  return (
    <section id="horario" className="py-20 sm:py-28 border-t border-zinc-100" data-testid="hours-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Overline>Horario de apertura</Overline>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-10">Cuándo encontrarnos</h2>
          <ul className="divide-y divide-zinc-200 border-y border-zinc-200">
            {settings.hours.map((h) => (
              <li key={h.day} data-testid={`hours-day-${h.day}`} className={`flex justify-between py-4 text-sm sm:text-base ${h.day === today ? "font-semibold text-[#111111]" : "text-zinc-600"}`}>
                <span>{DAY_NAMES[h.day]}{h.day === today && <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-zinc-400">Hoy</span>}</span>
                <span className="font-mono">{h.closed ? "Cerrado" : `${h.open} – ${h.close}`}</span>
              </li>
            ))}
          </ul>
        </div>
        <div id="contacto" className="lg:col-span-7 scroll-mt-24">
          <Overline>Dirección y contacto</Overline>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-6">Ven a vernos</h2>
          <p className="flex items-start gap-3 text-zinc-700 mb-6" data-testid="address-text"><MapPin className="w-5 h-5 mt-0.5 shrink-0" /> {ADDRESS}</p>
          <div className="aspect-[16/10] bg-zinc-100 border border-zinc-200 overflow-hidden">
            <iframe title="Mapa P7 BARBING SALON" src={MAPS_EMBED} className="w-full h-full grayscale" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="map-iframe" />
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" data-testid="google-maps-link" className="h-12 px-6 inline-flex items-center gap-2 border border-[#111111] text-xs tracking-[0.2em] uppercase hover:bg-[#111111] hover:text-white transition-colors">
              <Navigation className="w-4 h-4" /> Cómo llegar
            </a>
            {wa && (
              <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-contact-button" className="h-12 px-6 inline-flex items-center gap-2 bg-[#111111] text-white text-xs tracking-[0.2em] uppercase hover:bg-zinc-800 transition-colors">
                <MessageCircle className="w-4 h-4" /> Escríbenos por WhatsApp
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Follow = ({ settings }) => {
  const links = socialLinks(settings);
  if (!links.length) return null;
  return (
    <section className="py-20 bg-[#F8F9FA] border-t border-zinc-100" data-testid="social-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Overline>Redes sociales</Overline>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-10">Síguenos</h2>
        <div className="flex flex-wrap gap-4">
          {links.map(({ key, label, url, Icon }) => (
            <a key={key} href={url} target="_blank" rel="noopener noreferrer" data-testid={`social-link-${key}`} className="h-14 px-6 inline-flex items-center gap-3 bg-white border border-zinc-200 hover:border-[#111111] transition-colors">
              <Icon className="w-5 h-5" /> <span className="text-sm">{label}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

const CtaBand = () => (
  <section className="py-20 sm:py-24 border-t border-zinc-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center justify-between gap-8">
      <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-xl">Tu próximo corte, a un clic.</h2>
      <Link to="/reservar" data-testid="cta-book-button" className="h-14 px-10 inline-flex items-center justify-center bg-[#111111] text-white text-xs tracking-[0.25em] uppercase hover:bg-zinc-800 transition-colors">
        Reservar cita
      </Link>
    </div>
  </section>
);

export default function Home() {
  const { info, error, reload } = useInfo();
  if (error)
    return (
      <div className="py-32 text-center" data-testid="home-error">
        <p className="text-zinc-600">No se pudo cargar la información.</p>
        <button onClick={reload} className="mt-4 underline">Reintentar</button>
      </div>
    );
  if (!info) return <div className="py-32 text-center text-zinc-400" data-testid="home-loading">Cargando…</div>;
  return (
    <>
      <Hero />
      <Services services={info.services} />
      <Gallery items={info.gallery} />
      <HoursAndLocation settings={info.settings} />
      <Follow settings={info.settings} />
      <CtaBand />
    </>
  );
}
